import { useCallback, useEffect, useState } from 'react';
import {
  addOutOfNetworkSupplier,
  listLocationSuppliers
} from '../Api/Inventory.api';
import type {
  AddOutOfNetworkSupplierPayload,
  LocationSupplier
} from '../Types';

export const useLocationSuppliers = (
  sessionToken: string | null,
  branchId: string | undefined,
  enabled: boolean
) => {
  const [suppliers, setSuppliers] = useState<LocationSupplier[]>([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    if (!enabled || !sessionToken || !branchId) {
      setSuppliers([]);
      return;
    }

    setLoading(true);
    try {
      const rows = await listLocationSuppliers(branchId, sessionToken);
      setSuppliers(rows);
    } catch {
      setSuppliers([]);
    } finally {
      setLoading(false);
    }
  }, [enabled, sessionToken, branchId]);

  useEffect(() => {
    void load();
  }, [load]);

  const createOutOfNetworkSupplier = useCallback(
    async (payload: AddOutOfNetworkSupplierPayload) => {
      if (!sessionToken || !branchId) {
        throw new Error('Session required');
      }

      setAdding(true);
      try {
        const created = await addOutOfNetworkSupplier(
          branchId,
          payload,
          sessionToken
        );
        setSuppliers(prev => {
          if (prev.some(s => s.supplier_id === created.supplier_id)) {
            return prev;
          }
          return [...prev, created];
        });
        return created;
      } finally {
        setAdding(false);
      }
    },
    [branchId, sessionToken]
  );

  return {
    suppliers,
    loading,
    adding,
    reload: load,
    createOutOfNetworkSupplier
  };
};
